import React, { createContext, useState, useMemo, useContext, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';

interface ThemeContextType {
  mode: PaletteMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
});

export const useThemeMode = () => useContext(ThemeContext);

export const GamingThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get initial theme from localStorage or default to 'light' for elegant light blue/white
  const [mode, setMode] = useState<PaletteMode>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode as PaletteMode) || 'light';
  });

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Gaming-themed color palette
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === 'light'
            ? {
                // Light Mode - Elegant Light Blue & White
                primary: {
                  main: '#4A90E2', // Elegant Light Blue
                  light: '#6BA3E8',
                  dark: '#357ABD',
                  contrastText: '#FFFFFF',
                },
                secondary: {
                  main: '#87CEEB', // Sky Blue
                  light: '#B0E0E6',
                  dark: '#6BB6D6',
                  contrastText: '#FFFFFF',
                },
                background: {
                  default: '#F5F9FC', // Very Light Blue-White
                  paper: '#FFFFFF',
                },
                success: {
                  main: '#52C9A2', // Teal Green
                  light: '#7DD3B0',
                  dark: '#3FA580',
                },
                warning: {
                  main: '#FFB84D', // Soft Orange
                  light: '#FFC870',
                  dark: '#FF9900',
                },
                error: {
                  main: '#FF6B6B', // Soft Red
                  light: '#FF8E8E',
                  dark: '#E84848',
                },
                info: {
                  main: '#4A90E2', // Light Blue
                  light: '#6BA3E8',
                  dark: '#357ABD',
                },
                text: {
                  primary: '#2C3E50', // Dark Blue-Gray
                  secondary: '#5D6D7E',
                },
                divider: '#E8EDF2',
              }
            : {
                // Dark Mode - Elegant Dark Blue Theme
                primary: {
                  main: '#6BB6FF', // Brighter Light Blue for dark mode
                  light: '#8DC8FF',
                  dark: '#4A9EFF',
                  contrastText: '#FFFFFF',
                },
                secondary: {
                  main: '#9DD1FF', // Light Sky Blue
                  light: '#B8DEFF',
                  dark: '#7BB8FF',
                  contrastText: '#1A2B3C',
                },
                background: {
                  default: '#1A2B3C', // Dark Blue-Gray Background
                  paper: '#2C3E50', // Dark Blue-Gray Paper
                },
                success: {
                  main: '#5DD5A8', // Brighter Teal Green for dark mode
                  light: '#7EE0B8',
                  dark: '#45C894',
                },
                warning: {
                  main: '#FFC866', // Brighter Orange for dark mode
                  light: '#FFD68A',
                  dark: '#FFB84D',
                },
                error: {
                  main: '#FF7B7B', // Brighter Red for dark mode
                  light: '#FF9999',
                  dark: '#FF5D5D',
                },
                info: {
                  main: '#6BB6FF', // Brighter Light Blue for dark mode
                  light: '#8DC8FF',
                  dark: '#4A9EFF',
                },
                text: {
                  primary: '#E8F0F5', // Light text for dark background
                  secondary: '#B8C8D5', // Lighter secondary text
                },
                divider: '#3F5C7A', // Dark divider for dark mode
              }),
        },
        typography: {
          fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontWeight: 800,
            fontSize: '2.5rem',
            letterSpacing: '-0.02em',
          },
          h2: {
            fontWeight: 700,
            fontSize: '2rem',
            letterSpacing: '-0.01em',
          },
          h3: {
            fontWeight: 700,
            fontSize: '1.75rem',
          },
          h4: {
            fontWeight: 600,
            fontSize: '1.5rem',
          },
          h5: {
            fontWeight: 600,
            fontSize: '1.25rem',
          },
          h6: {
            fontWeight: 600,
            fontSize: '1rem',
          },
          button: {
            textTransform: 'none',
            fontWeight: 600,
          },
        },
        shape: {
          borderRadius: 16,
        },
        shadows: [
          'none',
          mode === 'light'
            ? '0 2px 8px rgba(74, 144, 226, 0.1)'
            : '0 2px 8px rgba(0, 0, 0, 0.3)',
          mode === 'light'
            ? '0 4px 12px rgba(74, 144, 226, 0.12)'
            : '0 4px 12px rgba(0, 0, 0, 0.35)',
          mode === 'light'
            ? '0 6px 16px rgba(74, 144, 226, 0.14)'
            : '0 6px 16px rgba(0, 0, 0, 0.4)',
          mode === 'light'
            ? '0 8px 20px rgba(74, 144, 226, 0.16)'
            : '0 8px 20px rgba(0, 0, 0, 0.45)',
          mode === 'light'
            ? '0 10px 24px rgba(74, 144, 226, 0.18)'
            : '0 10px 24px rgba(0, 0, 0, 0.5)',
          mode === 'light'
            ? '0 12px 28px rgba(74, 144, 226, 0.2)'
            : '0 12px 28px rgba(0, 0, 0, 0.55)',
          mode === 'light'
            ? '0 14px 32px rgba(74, 144, 226, 0.22)'
            : '0 14px 32px rgba(0, 0, 0, 0.6)',
          mode === 'light'
            ? '0 16px 36px rgba(74, 144, 226, 0.24)'
            : '0 16px 36px rgba(0, 0, 0, 0.65)',
          mode === 'light'
            ? '0 18px 40px rgba(74, 144, 226, 0.26)'
            : '0 18px 40px rgba(0, 0, 0, 0.7)',
          mode === 'light'
            ? '0 20px 44px rgba(74, 144, 226, 0.28)'
            : '0 20px 44px rgba(0, 0, 0, 0.75)',
          mode === 'light'
            ? '0 22px 48px rgba(74, 144, 226, 0.3)'
            : '0 22px 48px rgba(0, 0, 0, 0.8)',
          mode === 'light'
            ? '0 24px 52px rgba(74, 144, 226, 0.32)'
            : '0 24px 52px rgba(0, 0, 0, 0.85)',
          mode === 'light'
            ? '0 26px 56px rgba(74, 144, 226, 0.34)'
            : '0 26px 56px rgba(0, 0, 0, 0.9)',
          mode === 'light'
            ? '0 28px 60px rgba(74, 144, 226, 0.36)'
            : '0 28px 60px rgba(0, 0, 0, 0.95)',
          mode === 'light'
            ? '0 30px 64px rgba(74, 144, 226, 0.38)'
            : '0 30px 64px rgba(0, 0, 0, 1)',
          mode === 'light'
            ? '0 32px 68px rgba(74, 144, 226, 0.4)'
            : '0 32px 68px rgba(0, 0, 0, 1)',
          mode === 'light'
            ? '0 34px 72px rgba(74, 144, 226, 0.42)'
            : '0 34px 72px rgba(0, 0, 0, 1)',
          mode === 'light'
            ? '0 36px 76px rgba(74, 144, 226, 0.44)'
            : '0 36px 76px rgba(0, 0, 0, 1)',
          mode === 'light'
            ? '0 38px 80px rgba(74, 144, 226, 0.46)'
            : '0 38px 80px rgba(0, 0, 0, 1)',
          mode === 'light'
            ? '0 40px 84px rgba(74, 144, 226, 0.48)'
            : '0 40px 84px rgba(0, 0, 0, 1)',
          mode === 'light'
            ? '0 42px 88px rgba(74, 144, 226, 0.5)'
            : '0 42px 88px rgba(0, 0, 0, 1)',
          mode === 'light'
            ? '0 44px 92px rgba(74, 144, 226, 0.52)'
            : '0 44px 92px rgba(0, 0, 0, 1)',
          mode === 'light'
            ? '0 46px 96px rgba(74, 144, 226, 0.54)'
            : '0 46px 96px rgba(0, 0, 0, 1)',
          mode === 'light'
            ? '0 48px 100px rgba(74, 144, 226, 0.56)'
            : '0 48px 100px rgba(0, 0, 0, 1)',
        ],
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                padding: '10px 24px',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: mode === 'light' 
                  ? '0 4px 12px rgba(74, 144, 226, 0.15)'
                  : '0 4px 12px rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: mode === 'light'
                    ? '0 6px 16px rgba(74, 144, 226, 0.2)'
                    : '0 6px 16px rgba(0, 0, 0, 0.6)',
                },
                transition: 'all 0.3s ease',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 20,
                boxShadow: mode === 'light'
                  ? '0 4px 20px rgba(74, 144, 226, 0.1)'
                  : '0 4px 20px rgba(0, 0, 0, 0.5)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: mode === 'light'
                    ? '0 8px 30px rgba(74, 144, 226, 0.18)'
                    : '0 8px 30px rgba(0, 0, 0, 0.7)',
                },
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#FFFFFF' : '#2C3E50',
                color: mode === 'light' ? '#2C3E50' : '#E8F0F5',
                boxShadow: mode === 'light'
                  ? '0 2px 8px rgba(74, 144, 226, 0.1)'
                  : '0 2px 8px rgba(0, 0, 0, 0.5)',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};

